import React, { useState } from 'react';
import Header from '../components/Layout/Header';
import {
  Copy, Check, Play, Terminal, ChevronDown, ChevronRight,
  Zap, FileText, Mic, MessageSquare,
  CheckCircle, XCircle, Loader2, Search
} from 'lucide-react';

interface CurlCommand {
  title: string;
  description: string;
  command: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'WS';
}

interface CurlSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  color: string;
  commands: CurlCommand[];
}

const METHOD_COLORS: Record<string, string> = {
  GET: 'bg-emerald-100 text-emerald-700',
  POST: 'bg-blue-100 text-blue-700',
  PUT: 'bg-amber-100 text-amber-700',
  DELETE: 'bg-red-100 text-red-700',
  PATCH: 'bg-purple-100 text-purple-700',
};

const CURL_SECTIONS: CurlSection[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: <Zap size={18} />,
    color: 'from-blue-500 to-cyan-500',
    commands: [
      { title: 'Health Check', description: 'Verify the API is running', command: 'curl https://vidhiguruapi.vidhiguru.in/health', method: 'GET' },
      { title: 'List Your Agents', description: 'See which AI agents are assigned to your account', command: 'curl https://vidhiguruapi.vidhiguru.in/api/v1/agents \\\n  -H "x-api-key: YOUR_API_KEY"', method: 'GET' },
      { title: 'Get Pricing', description: 'Check current token pricing', command: 'curl https://vidhiguruapi.vidhiguru.in/api/pricing', method: 'GET' },
    ],
  },
  {
    id: 'chat-queries',
    title: 'AI Chat Queries',
    icon: <MessageSquare size={18} />,
    color: 'from-indigo-500 to-purple-500',
    commands: [
      { title: 'Simple Chat (Non-Streaming)', description: 'Send a query and get a complete response', command: 'curl -X POST https://vidhiguruapi.vidhiguru.in/api/v1/agents/junior/chat \\\n  -H "x-api-key: YOUR_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"query": "What is Section 302 of BNS?"}\'', method: 'POST' },
      { title: 'Streaming Chat (SSE)', description: 'Stream response token-by-token (for real-time UIs)', command: 'curl -N -X POST https://vidhiguruapi.vidhiguru.in/api/v1/agents/junior/chat/stream \\\n  -H "x-api-key: YOUR_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"query": "Explain anticipatory bail under BNSS"}\'', method: 'POST' },
      { title: 'Pro Model (Complex Analysis)', description: 'Use the Pro model for detailed legal research', command: 'curl -X POST https://vidhiguruapi.vidhiguru.in/api/v1/agents/junior/chat/pro \\\n  -H "x-api-key: YOUR_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"query": "Compare Section 498A IPC with equivalent BNS provision. Include recent SC judgments."}\'', method: 'POST' },
      { title: 'Pro Streaming', description: 'Stream Pro model response', command: 'curl -N -X POST https://vidhiguruapi.vidhiguru.in/api/v1/agents/junior/chat/pro/stream \\\n  -H "x-api-key: YOUR_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"query": "Draft a bail application for an accused under Section 302 BNS"}\'', method: 'POST' },
    ],
  },
  {
    id: 'ocr',
    title: 'Document OCR',
    icon: <FileText size={18} />,
    color: 'from-emerald-500 to-teal-500',
    commands: [
      { title: 'OCR — Extract Text from Image', description: 'Send a base64-encoded image to extract text', command: 'curl -X POST https://vidhiguruapi.vidhiguru.in/api/ocr/extract \\\n  -H "x-api-key: YOUR_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"image": "BASE64_IMAGE_DATA", "mimeType": "image/jpeg"}\'', method: 'POST' },
    ],
  },
  {
    id: 'docai',
    title: 'DocAI — Multimodal Document Query',
    icon: <FileText size={18} />,
    color: 'from-sky-500 to-blue-600',
    commands: [
      { title: 'DocAI — Ask a Question About Files', description: 'Send files (base64) + a text question in one call. Gemini reads them natively and answers. +1 token surcharge per request.', command: 'curl -X POST https://vidhiguruapi.vidhiguru.in/api/query/docai \\\n  -H "x-api-key: YOUR_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d \'{\n    "query": "What are the key terms in this contract?",\n    "files": [{"mimeType": "application/pdf", "data": "BASE64_FILE_DATA", "name": "contract.pdf"}]\n  }\'', method: 'POST' },
      { title: 'DocAI Streaming (SSE)', description: 'Same input as above; the answer streams back as Server-Sent Events. Accepted types: JPEG, PNG, WebP, GIF, PDF (max 50MB total).', command: 'curl -N -X POST https://vidhiguruapi.vidhiguru.in/api/query/docai/stream \\\n  -H "x-api-key: YOUR_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d \'{\n    "query": "Summarize this document in 5 bullet points",\n    "files": [{"mimeType": "image/jpeg", "data": "BASE64_FILE_DATA", "name": "page1.jpg"}]\n  }\'', method: 'POST' },
    ],
  },
  {
    id: 'voice',
    title: 'Voice AI (WebSocket)',
    icon: <Mic size={18} />,
    color: 'from-purple-500 to-pink-500',
    commands: [
      { title: 'Connect to Voice WebSocket', description: 'Use wscat to start a real-time voice session', command: 'wscat -c wss://vidhiguruapi.vidhiguru.in/live-voice', method: 'WS' },
    ],
  },
];
const CommandCard: React.FC<{ cmd: CurlCommand }> = ({ cmd }) => {
  const [copied, setCopied] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<{ status: 'success' | 'error'; data: string } | null>(null);
  const [showResult, setShowResult] = useState(false);

  const copyCommand = () => {
    navigator.clipboard.writeText(cmd.command);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const runCommand = async () => {
    setRunning(true);
    setResult(null);
    setShowResult(true);

    try {
      // Parse the curl command to extract method, url, headers, body
      const lines = cmd.command.replace(/\\\n\s*/g, ' ').trim();
      const urlMatch = lines.match(/(?:curl\s+(?:-s\s+)?(?:-X\s+\w+\s+)?)"?([^\s"]+)"?/);
      const url = urlMatch ? urlMatch[1] : '';

      const bodyMatch = lines.match(/-d\s+'({[\s\S]*?})'/);
      const body = bodyMatch ? JSON.parse(bodyMatch[1]) : undefined;

      const headerMatches = lines.match(/-H\s+"([^"]+)"/g) || [];
      const headers: Record<string, string> = {};
      headerMatches.forEach((m) => {
        const inner = m.replace(/-H\s+"/, '').replace(/"$/, '');
        const [key, ...rest] = inner.split(': ');
        if (key && rest.length) headers[key] = rest.join(': ');
      });

      const fetchOptions: RequestInit = {
        method: cmd.method,
        headers: { 'Content-Type': 'application/json', ...headers },
      };
      if (body && cmd.method !== 'GET') {
        fetchOptions.body = JSON.stringify(body);
      }

      const response = await fetch(url, fetchOptions);
      const data = await response.json();
      setResult({
        status: response.ok ? 'success' : 'error',
        data: JSON.stringify(data, null, 2),
      });
    } catch (err: any) {
      setResult({
        status: 'error',
        data: `Error: ${err.message}`,
      });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-all">
      <div className="flex items-center gap-3 p-4">
        <span className={`px-2.5 py-1 rounded text-xs font-bold ${METHOD_COLORS[cmd.method]}`}>
          {cmd.method}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900">{cmd.title}</div>
          <div className="text-xs text-gray-500">{cmd.description}</div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={runCommand}
            disabled={running || cmd.command.includes('<BASE64')}
            className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title={cmd.command.includes('<BASE64') ? 'Requires real audio data' : 'Run this command'}
          >
            {running ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
            Run
          </button>
          <button
            onClick={copyCommand}
            className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-gray-800 text-white hover:bg-gray-700 transition-colors"
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>

      {/* Command block */}
      <div className="bg-gray-900 px-4 py-3 overflow-x-auto">
        <pre className="text-xs text-gray-300 font-mono whitespace-pre">{cmd.command}</pre>
      </div>

      {/* Result */}
      {showResult && (
        <div className="border-t border-gray-200">
          <button
            onClick={() => setShowResult(false)}
            className="w-full flex items-center gap-2 px-4 py-2 text-xs text-gray-500 hover:bg-gray-50"
          >
            {result ? (
              result.status === 'success' ? (
                <CheckCircle size={14} className="text-emerald-500" />
              ) : (
                <XCircle size={14} className="text-red-500" />
              )
            ) : (
              <Loader2 size={14} className="animate-spin" />
            )}
            <span>{result ? 'Response' : 'Running...'}</span>
            <span className="ml-auto text-gray-400">click to dismiss</span>
          </button>
          {result && (
            <div className="bg-gray-50 px-4 py-3 max-h-64 overflow-auto">
              <pre className={`text-xs font-mono whitespace-pre-wrap ${result.status === 'error' ? 'text-red-600' : 'text-gray-700'}`}>
                {result.data}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CurlCommandsPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredSections = CURL_SECTIONS.map((section) => ({
    ...section,
    commands: section.commands.filter(
      (cmd) =>
        !searchQuery ||
        cmd.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cmd.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cmd.command.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter((s) => s.commands.length > 0);

  const totalCommands = CURL_SECTIONS.reduce((sum, s) => sum + s.commands.length, 0);

  return (
    <div className="flex flex-col h-full">
      <Header title="cURL Commands" subtitle="Ready-to-run commands for testing all API endpoints" />

      <div className="flex-1 overflow-y-auto">
        {/* Hero */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white px-8 py-8">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-white/10 rounded-lg">
                <Terminal size={24} />
              </div>
              <div>
                <h2 className="text-xl font-semibold">cURL Command Reference</h2>
                <p className="text-gray-400 text-sm">
                  {CURL_SECTIONS.length} sections · {totalCommands} commands · Copy or run directly
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-5">
              <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold">{totalCommands}</div>
                <div className="text-xs text-gray-400 mt-1">Total Commands</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold">2</div>
                <div className="text-xs text-gray-400 mt-1">API Services</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold flex items-center justify-center gap-1">
                  <Play size={18} /> Live
                </div>
                <div className="text-xs text-gray-400 mt-1">Run In-Browser</div>
              </div>
            </div>

            {/* Search */}
            <div className="mt-5 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                placeholder="Search commands by name, description, or content..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white/10 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-white/30"
              />
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="max-w-5xl mx-auto px-8 py-6 space-y-6 pb-20">
          {filteredSections.map((section) => {
            const isOpen = activeSection === section.id || searchQuery.length > 0;
            return (
              <div key={section.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <button
                  onClick={() => setActiveSection(isOpen && !searchQuery ? null : section.id)}
                  className="w-full flex items-center gap-4 p-5 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className={`p-2.5 rounded-lg bg-gradient-to-br ${section.color} text-white`}>
                    {section.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{section.title}</h3>
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                        {section.commands.length} commands
                      </span>
                    </div>
                  </div>
                  {isOpen ? <ChevronDown size={18} className="text-gray-400" /> : <ChevronRight size={18} className="text-gray-400" />}
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100 p-4 space-y-3">
                    {section.commands.map((cmd, i) => (
                      <CommandCard key={i} cmd={cmd} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {filteredSections.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <Terminal size={40} className="mx-auto mb-3 opacity-50" />
              <p>No commands match "{searchQuery}"</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CurlCommandsPage;
